<template>
	<div class="d-flex flex-column align-center justify-center text-center pa-8 ga-4 async-reminder-empty-state">
		<div class="text-body-1 text-medium-emphasis">
			It's been {{ elapsedLabel }}{{ sentClause }}. Would you like to send a reminder to your customer?
		</div>
		<v-btn
			v-test="'async-reminder-send'"
			v-track="'async-reminder-send-button'"
			color="primary"
			variant="flat"
			rounded="xl"
			:loading="sending"
			@click="onSendReminder">
			Send a Reminder
		</v-btn>
	</div>
</template>

<script setup lang="ts">
	import { computed, getCurrentInstance, ref } from "vue";
	import { useToast } from "vue-toastification";
	import { useNow } from "@vueuse/core";
	import moment from "moment-timezone";
	import api from "@/api";
	import { calculateTimeout } from "@/helpers/utilities";
	import { notifyInvitationDelivery } from "@/helpers/invitationSkipReasons";
	import { formatAsyncReminderElapsed } from "@/helpers/time";
	import type { Conversation } from "@/types/Conversation";

	const props = defineProps<{
		conversation: Conversation;
		freeTrialEnded: boolean;
		me: any;
	}>();
	// LSP-20787 (22015): the page refetches the delivery state after a reminder, as it does for EntityActions' resend.
	const emit = defineEmits<{ (event: "invitationSent", conversationId: string): void }>();

	const toast = useToast();
	const instance = getCurrentInstance();
	const sending = ref(false);
	// Reactive clock so the elapsed label refreshes as the page stays open across day/week/month boundaries.
	const now = useNow({ interval: 60_000 });

	// Mirror EntityActions.sendInvitation's noun split so the prompt and toast copy line up with the
	// "Resend Request" / "Resend Invitation" menu label the user sees elsewhere for the same
	// conversation. AsyncReminderEmptyState only renders for async conversations
	// (gated in EntityDetailPage.showAsyncReminderPrompt), so the FORM/non-FORM split here is
	// equivalent to EntityActions' `isAsync && !isWebformConversation ? "request" : "invitation"`.
	const reminderNoun = computed(() => (props.conversation.sourceType === "FORM" ? "invitation" : "request"));

	/**
	 * Source types where the ADMIN actually sent something the elapsed clock can be measured from.
	 *
	 * LSP-17986 review (ls-heroku-ci, high x2): the prompt says "since you sent the request", but
	 * `showAsyncReminderPrompt` in EntityDetailPage never filters on `sourceType`, so a conversation the
	 * CUSTOMER started reaches this same prompt. For those the admin never sent anything and the sentence
	 * would be flatly untrue — the exact failure this ticket set out to remove (naming an artifact the admin
	 * then goes looking for and cannot find). The clause is omitted there rather than guessed at.
	 *
	 * FORM belongs on the customer side, which round 1 of this change got wrong. It is tempting to read
	 * `reminderNoun`'s FORM -> "invitation" mapping as evidence that an invitation was sent, but that noun
	 * exists for the toast fired AFTER the admin clicks — it describes the send just made, not a prior one.
	 * The codebase is consistent that FORM is customer self-serve:
	 *   - contact-api forms.ts: FORM is the DEFAULT sourceType for a public form submission, overridden to
	 *     KIOSK / QRCODE / WEBSITE when those ids are set — so all four come from the SAME self-serve
	 *     handler and belong on the same side of this list.
	 *   - contact-api conversations.ts / snapshots.ts name it `selfServeForm`, and it is exempt from
	 *     outbound rate limiting precisely because nothing goes out.
	 *   - contact-api notificationOwner.ts routes a FORM conversation's owner through
	 *     `defaultSelfServeUserId`, not an inviting admin.
	 *   - contact-admin EntityActions.vue excludes FORM from `isCustomerAsyncRequest` and offers it
	 *     "Copy Recording Link" where a real async request gets "Copy Request Link".
	 *
	 * That leaves SMS (a texted request) and UNKNOWN, the ENUM's default and what an admin-created async
	 * request carries when no more specific source applies. A null/undefined sourceType reads as UNKNOWN.
	 *
	 * Deliberately an ALLOW-list, not a deny-list of the customer-originated values: a new inbound source
	 * added to constants.SOURCE_TYPES later would silently inherit the false sentence under a deny-list,
	 * whereas here it just renders the shorter, always-true version until someone opts it in.
	 */
	const ADMIN_ORIGINATED_SOURCE_TYPES = ["SMS", "UNKNOWN"];

	/**
	 * " since you sent the request", or "" when nothing was sent.
	 *
	 * The noun is the literal "request", NOT `reminderNoun`: the only sources that reach this branch are SMS
	 * and UNKNOWN, both of which `reminderNoun` maps to "request", so interpolating it would imply an
	 * "invitation" reading that can no longer occur and invite someone to re-add FORM to the list above.
	 * `reminderNoun` still drives the toast copy, which is the case it was written for.
	 *
	 * Includes its own leading space so the template can concatenate without emitting a stray one in the
	 * omitted case — `It's been 2 days . Would you…` is the kind of detail that reads as a broken string.
	 */
	const sentClause = computed(() => {
		const sourceType = (props.conversation.sourceType ?? "UNKNOWN").toUpperCase();
		if (!ADMIN_ORIGINATED_SOURCE_TYPES.includes(sourceType)) return "";
		return " since you sent the request";
	});

	const elapsedLabel = computed(() => {
		const nowMoment = moment(now.value);
		const then = moment(props.conversation.createdAt);
		return formatAsyncReminderElapsed(nowMoment.diff(then, "minutes"));
	});

	async function onSendReminder() {
		if (props.freeTrialEnded) {
			(instance?.proxy?.$root as any).$freeTrialEndedDialog.open(props.me?.organization);
			return;
		}

		sending.value = true;
		// Captured before the await, so a send that finishes after an in-place session switch names the session it was for.
		const conversationId = props.conversation.id;
		try {
			const response = await api.sendInvitation(conversationId);
			emit("invitationSent", conversationId);
			// LSP-20787 (22015, 22029): a channel the API skipped shows the LSP-19707 skip summary instead of "re-sent".
			notifyInvitationDelivery(toast, response.invitations, `The ${reminderNoun.value} has been re-sent to the contact.`);
		} catch {
			const message = `Failed to re-send ${reminderNoun.value} to the contact.`;
			toast.error(message, { timeout: calculateTimeout(message) });
		} finally {
			sending.value = false;
		}
	}
</script>
